import { Link, useNavigate } from '@tanstack/react-router';
import { auth, signOutUser } from '@/lib/platform';
import { Sheet } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Icon, type IconName } from '@/components/ui/icon';
import { Segmented } from '@/components/ui/segmented';
import { useTheme, type Theme } from '@/lib/use-theme';

const THEME_OPTIONS: { value: Theme; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

// Primary nav lives inside this menu on mobile (the desktop sidebar owns nav
// ≥640px, where the .wb-menu-nav section is CSS-hidden).
const NAV: { to: string; label: string; icon: IconName; exact?: boolean }[] = [
  { to: '/', label: 'Book', icon: 'home', exact: true },
  { to: '/trips', label: 'My Trips', icon: 'trips' },
  { to: '/support', label: 'Support', icon: 'support' },
];

export function AccountSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const user = auth.currentUser;
  // Identity is the Google account — no profile lookup (that was weebee's
  // /api/v1/users/me, which this app no longer calls).
  const name = user?.displayName || 'Your account';
  const email = user?.email || '';

  async function handleSignOut() {
    onClose();
    await signOutUser();
    void navigate({ to: '/login' });
  }

  return (
    <Sheet open={open} onClose={onClose} title="Menu">
      <Link
        to="/account"
        className="wb-row wb-gap-14 wb-navitem"
        onClick={onClose}
        style={{ marginBottom: 18 }}
      >
        <div
          className="wb-leadicon"
          style={{
            width: 52,
            height: 52,
            background: 'var(--honey-wash)',
            color: 'var(--honey-700)',
          }}
        >
          <Icon name="user" size={26} />
        </div>
        <div className="wb-grow">
          <div className="wb-h3">{name}</div>
          {email && <div className="wb-small wb-mono">{email}</div>}
        </div>
        <Icon name="chevronRight" size={18} style={{ color: 'var(--ink-3)' }} />
      </Link>

      <nav className="wb-menu-nav" aria-label="Primary">
        {NAV.map((t) => (
          <Link
            key={t.to}
            to={t.to}
            activeOptions={{ exact: t.exact }}
            className="wb-navitem"
            activeProps={{ className: 'wb-navitem is-active' }}
            onClick={onClose}
          >
            <Icon name={t.icon} size={22} />
            <span className="wb-grow">{t.label}</span>
          </Link>
        ))}
      </nav>

      <div
        className="wb-card wb-card--flat"
        style={{ background: 'var(--surface-2)', padding: '4px 14px', marginBottom: 16 }}
      >
        <div className="wb-row wb-between" style={{ padding: '12px 0' }}>
          <span className="wb-row wb-gap-10">
            <Icon name="sparkle" size={19} style={{ color: 'var(--ink-2)' }} /> Appearance
          </span>
          <div style={{ width: 150 }}>
            <Segmented
              options={THEME_OPTIONS}
              value={theme}
              onChange={setTheme}
              ariaLabel="Appearance"
            />
          </div>
        </div>
      </div>

      <Link to="/about" className="wb-navitem" onClick={onClose} style={{ marginBottom: 10 }}>
        <Icon name="info" size={22} />
        <span className="wb-grow">About &amp; policies</span>
        <Icon name="chevronRight" size={18} style={{ color: 'var(--ink-3)' }} />
      </Link>

      <Button variant="secondary" block icon="logout" onClick={handleSignOut}>
        Sign out
      </Button>
    </Sheet>
  );
}
